import { useEffect, useMemo, useState } from "react";
import PageMeta from "../components/common/PageMeta";
import PageBreadcrumb from "../components/common/PageBreadCrumb";
import ComponentCard from "../components/common/ComponentCard";
import RegisterLoader from "../components/common/RegisterLoader";
import Label from "../components/form/Label";
import Input from "../components/form/input/InputField";
import { Modal } from "../components/ui/modal";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../components/ui/table";
import { useLanguage } from "../context/LanguageContext";
import { getLocalizedFallback } from "../utils/apiMessageI18n";
import {
  createRole,
  fetchPermissionsList,
  fetchRoleDetails,
  fetchRolesTableList,
  PermissionListItem,
  RoleTableItem,
  updateRolePermissions,
} from "../services/userService";
import { PlusIcon } from "../icons";

export default function Roles() {
  const { language } = useLanguage();
  const [roles, setRoles] = useState<RoleTableItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [permissionsRole, setPermissionsRole] = useState<RoleTableItem | null>(null);
  const [allPermissions, setAllPermissions] = useState<PermissionListItem[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(false);
  const [isPermissionsSaving, setIsPermissionsSaving] = useState(false);
  const [permissionsError, setPermissionsError] = useState("");

  const pageTitle = getLocalizedFallback(language, {
    pt: "Perfis",
    es: "Perfiles",
    en: "Roles",
  });

  const searchPlaceholder = getLocalizedFallback(language, {
    pt: "Pesquisar por perfil...",
    es: "Buscar por perfil...",
    en: "Search roles...",
  });

  const createRoleLabel = getLocalizedFallback(language, {
    pt: "Criar perfil",
    es: "Crear perfil",
    en: "Create role",
  });

  const createPanelDescription = getLocalizedFallback(language, {
    pt: "Preencha os dados do novo perfil.",
    es: "Complete los datos del nuevo perfil.",
    en: "Fill in the new role details.",
  });

  const closeLabel = getLocalizedFallback(language, {
    pt: "Fechar",
    es: "Cerrar",
    en: "Close",
  });

  const saveLabel = getLocalizedFallback(language, {
    pt: "Salvar alterações",
    es: "Guardar cambios",
    en: "Save changes",
  });

  const savingLabel = getLocalizedFallback(language, {
    pt: "Salvando...",
    es: "Guardando...",
    en: "Saving...",
  });

  const roleHeader = getLocalizedFallback(language, {
    pt: "Perfil",
    es: "Perfil",
    en: "Role",
  });

  const actionsHeader = getLocalizedFallback(language, {
    pt: "Ações",
    es: "Acciones",
    en: "Actions",
  });

  const permissionsButtonLabel = getLocalizedFallback(language, {
    pt: "Permissões",
    es: "Permisos",
    en: "Permissions",
  });

  const permissionsTitle = getLocalizedFallback(language, {
    pt: "Adicionar/remover permissões",
    es: "Agregar/quitar permisos",
    en: "Add/remove permissions",
  });

  const permissionsDescription = getLocalizedFallback(language, {
    pt: "Selecione as permissões ativas para este perfil.",
    es: "Seleccione los permisos activos para este perfil.",
    en: "Select active permissions for this role.",
  });

  const loadingPermissionsLabel = getLocalizedFallback(language, {
    pt: "Carregando permissões...",
    es: "Cargando permisos...",
    en: "Loading permissions...",
  });

  const savePermissionsLabel = getLocalizedFallback(language, {
    pt: "Salvar alterações",
    es: "Guardar cambios",
    en: "Save changes",
  });

  const savingPermissionsLabel = getLocalizedFallback(language, {
    pt: "Salvando...",
    es: "Guardando...",
    en: "Saving...",
  });

  const emptyLabel = getLocalizedFallback(language, {
    pt: "Nenhum perfil encontrado.",
    es: "No se encontraron perfiles.",
    en: "No roles found.",
  });

  const filteredRoles = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return roles;
    return roles.filter((role) => role.name.toLowerCase().includes(query));
  }, [roles, searchText]);

  const visibleRoles = useMemo(() => {
    return filteredRoles.filter((role) => role.name.toLowerCase() !== "administrador");
  }, [filteredRoles]);

  const canCreate = !isCreating && createName.trim().length > 0;

  const loadRoles = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const list = await fetchRolesTableList();
      setRoles(list);
    } catch (error) {
      setRoles([]);
      setErrorMessage(error instanceof Error ? error.message : "Não foi possível listar perfis.");
    } finally {
      setIsLoading(false);
    }
  };

  const openPermissionsPanel = async (role: RoleTableItem) => {
    setPermissionsRole(role);
    setIsPermissionsOpen(true);
    setPermissionsError("");
    setIsPermissionsLoading(true);
    try {
      const [permissions, roleDetails] = await Promise.all([
        fetchPermissionsList(),
        fetchRoleDetails({ id_role: role.id }),
      ]);
      setAllPermissions(permissions);
      const activeNames = new Set(permissions.map((item) => item.name));
      setSelectedPermissions(roleDetails.permissions.filter((name) => activeNames.has(name)));
    } catch (error) {
      setAllPermissions([]);
      setSelectedPermissions([]);
      setPermissionsError(
        error instanceof Error ? error.message : "Não foi possível carregar permissões."
      );
    } finally {
      setIsPermissionsLoading(false);
    }
  };

  const togglePermission = (name: string) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(name)) return prev.filter((item) => item !== name);
      return [...prev, name];
    });
  };

  useEffect(() => {
    void loadRoles();
  }, []);

  return (
    <>
      <PageMeta title={`${pageTitle} | Gerify`} description={pageTitle} />
      {(isLoading || isCreating) && <RegisterLoader />}
      <PageBreadcrumb pageTitle={pageTitle} />

      <ComponentCard
        headerLeft={
          <div className="flex w-full items-center gap-2 sm:w-[420px]">
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-10"
            />
            <button
              type="button"
              aria-label={searchPlaceholder}
              title={searchPlaceholder}
              onClick={() => setSearchText((prev) => prev.trim())}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-500 text-white hover:bg-brand-600"
            >
              <svg
                className="fill-white"
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                  fill=""
                />
              </svg>
            </button>
          </div>
        }
        headerRight={
          <button
            type="button"
            onClick={() => {
              setCreateName("");
              setCreateError("");
              setIsCreateOpen(true);
            }}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600"
          >
            <PlusIcon className="h-4 w-4 fill-white" />
            <span>{createRoleLabel}</span>
          </button>
        }
      >
        {errorMessage && (
          <p className="text-sm text-error-500" role="alert">
            {errorMessage}
          </p>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="max-w-full overflow-x-auto">
            <div className="min-w-[520px]">
              <Table>
                <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                  <TableRow>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      {roleHeader}
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400"
                    >
                      {actionsHeader}
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                  {visibleRoles.length === 0 ? (
                    <TableRow>
                      <TableCell className="px-5 py-6 text-gray-500 text-theme-sm dark:text-gray-400">
                        {emptyLabel}
                      </TableCell>
                      <TableCell className="px-5 py-6"> </TableCell>
                    </TableRow>
                  ) : (
                    visibleRoles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell className="px-5 py-4 text-start">
                          <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                            {role.name}
                          </span>
                        </TableCell>
                        <TableCell className="px-5 py-4 text-end">
                          <button
                            type="button"
                            onClick={() => void openPermissionsPanel(role)}
                            aria-label={permissionsButtonLabel}
                            title={permissionsButtonLabel}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 15.75C10.2051 15.75 8.75 14.2949 8.75 12.5C8.75 10.7051 10.2051 9.25 12 9.25C13.7949 9.25 15.25 10.7051 15.25 12.5C15.25 14.2949 13.7949 15.75 12 15.75Z"
                                stroke="currentColor"
                                strokeWidth="1.8"
                              />
                              <path
                                d="M19.25 12.5C19.25 13.0492 19.1597 13.5787 18.9932 14.0739L20.5 15.2981L18.999 17.8976L17.1547 17.0924C16.3955 17.7179 15.4764 18.1586 14.471 18.3553L14.25 20.5H11.25L11.029 18.3553C10.0236 18.1586 9.10449 17.7179 8.34535 17.0924L6.50102 17.8976L5 15.2981L6.50681 14.0739C6.34026 13.5787 6.25 13.0492 6.25 12.5C6.25 11.9508 6.34026 11.4213 6.50681 10.9261L5 9.70192L6.50102 7.10241L8.34535 7.90759C9.10449 7.28212 10.0236 6.84137 11.029 6.64472L11.25 4.5H14.25L14.471 6.64472C15.4764 6.84137 16.3955 7.28212 17.1547 7.90759L18.999 7.10241L20.5 9.70192L18.9932 10.9261C19.1597 11.4213 19.25 11.9508 19.25 12.5Z"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </ComponentCard>

      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} className="max-w-[720px] p-6 lg:p-8">
        <h4 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white/90">{createRoleLabel}</h4>
        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">{createPanelDescription}</p>

        <div className="space-y-5">
          <div>
            <Label htmlFor="create-role-name">{roleHeader}</Label>
            <Input
              id="create-role-name"
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
            />
          </div>

          {createError && (
            <p className="text-sm text-error-500" role="alert">
              {createError}
            </p>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setIsCreateOpen(false)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              {closeLabel}
            </button>
            <button
              type="button"
              disabled={!canCreate}
              onClick={async () => {
                if (!canCreate) return;
                setIsCreating(true);
                setCreateError("");
                try {
                  await createRole({ name: createName.trim() });
                  setIsCreateOpen(false);
                  setCreateName("");
                  await loadRoles();
                } catch (error) {
                  setCreateError(error instanceof Error ? error.message : "Não foi possível criar perfil.");
                } finally {
                  setIsCreating(false);
                }
              }}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCreating ? savingLabel : saveLabel}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPermissionsOpen}
        onClose={() => setIsPermissionsOpen(false)}
        className="max-w-[920px] p-6 lg:p-8"
      >
        <h4 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white/90">{permissionsTitle}</h4>
        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
          {permissionsRole ? `${permissionsRole.name} • ${permissionsDescription}` : permissionsDescription}
        </p>

        {permissionsError && (
          <p className="mb-4 text-sm text-error-500" role="alert">
            {permissionsError}
          </p>
        )}

        {isPermissionsLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{loadingPermissionsLabel}</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {allPermissions.map((permission) => {
              const isChecked = selectedPermissions.includes(permission.name);
              const label = permission.description || permission.name;
              return (
                <label
                  key={permission.id}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border bg-white px-3 py-2 text-sm text-gray-800 shadow-theme-xs dark:bg-white/[0.03] dark:text-white/90 ${
                    isChecked
                      ? "border-brand-500"
                      : "border-gray-200 hover:border-brand-300 dark:border-gray-800 dark:hover:border-brand-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => togglePermission(permission.name)}
                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:outline-none focus:ring-0 dark:border-gray-700 dark:bg-gray-800"
                  />
                  <span className="leading-tight">{label}</span>
                </label>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setIsPermissionsOpen(false)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            {closeLabel}
          </button>
          <button
            type="button"
            disabled={!permissionsRole || isPermissionsLoading || isPermissionsSaving}
            onClick={async () => {
              if (!permissionsRole) return;
              if (isPermissionsSaving) return;
              setIsPermissionsSaving(true);
              setPermissionsError("");
              try {
                const unique = Array.from(new Set(selectedPermissions)).sort();
                await updateRolePermissions({ id_role: permissionsRole.id, permissions: unique });
                setIsPermissionsOpen(false);
              } catch (error) {
                setPermissionsError(
                  error instanceof Error ? error.message : "Não foi possível salvar permissões."
                );
              } finally {
                setIsPermissionsSaving(false);
              }
            }}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isPermissionsSaving ? savingPermissionsLabel : savePermissionsLabel}
          </button>
        </div>
      </Modal>
    </>
  );
}
